import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
	Drawer,
	Input,
	Switch,
	DatePicker,
	Checkbox,
	Divider,
	Select,
	message,
	Tooltip,
} from 'antd';
import {
	RedoOutlined,
	PlusOutlined,
	ExportOutlined,
	EyeOutlined,
	CloseCircleOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { Box, Flex, Text } from 'atoms';
import { FilterAtnButton } from 'utils/commonStyled';
import { FilterTag } from 'components/common';
import { Container, AntdTable } from 'components/custom-table/Style';
import { getClientListV2, exportClientCSV, updateClientStatus } from 'store/clientSliceV2';
import { usePersistedState } from 'hooks';
import { StatusChangeModal } from 'componentsv2/modals';

const { RangePicker } = DatePicker;
const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;

const ViewButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #1890ff;
`;

const CreateButton = styled(FilterAtnButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 0 20px;
	min-width: 120px;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

// Filter Drawer Styled Components
const FilterHeader = styled(Flex)`
	justify-content: space-between;
	align-items: center;
	padding: 16px 0;
`;

const FilterTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const FilterCloseButton = styled(CloseCircleOutlined)`
	font-size: 20px;
	color: #999;
	cursor: pointer;

	&:hover {
		color: #666;
	}
`;

const FilterSection = styled(Box)`
	margin-bottom: 16px;
`;

const FilterSectionTitle = styled(Text)`
	font-size: 14px;
	font-weight: 500;
	color: #333;
	margin-bottom: 12px;
	display: block;
`;

const StyledRangePicker = styled(RangePicker)`
	width: 100%;
	height: 40px;
`;

const CheckboxWrapper = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
	margin-left: 0 !important;

	.ant-checkbox + span {
		font-size: 14px;
		color: #333;
	}
`;

const StyledSelect = styled(Select)`
	width: 100%;

	.ant-select-selector {
		height: 40px !important;
		display: flex;
		align-items: center;
	}
`;

const ResetButton = styled(FilterAtnButton)`
	width: 100%;
	margin-top: 24px;
`;

// Table columns configuration
const getTableColumns = (navigate, handleStatusToggle) => [
	{
		title: 'Client Legal Name',
		dataIndex: 'client_legal_name',
		key: 'client_legal_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Company Name',
		dataIndex: 'company_name',
		key: 'company_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Legal Structure',
		dataIndex: 'legal_structure',
		key: 'legal_structure',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Primary Cost Center',
		dataIndex: 'primary_cost_center',
		key: 'primary_cost_center',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Primary PoC Name',
		dataIndex: 'primary_poc_name',
		key: 'primary_poc_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Primary PoC Number',
		dataIndex: 'primary_poc_contact',
		key: 'primary_poc_contact',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Email Id',
		dataIndex: 'email_id',
		key: 'email_id',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'City',
		dataIndex: 'city',
		key: 'city',
		render: (text, record) => {
			// Extract city from head_office_address object if available, otherwise use direct city field
			const city = record?.head_office_address?.city || text || '-';
			return <Text>{city}</Text>;
		},
	},
	{
		title: 'Created at',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (text) => {
			if (!text) return <Text>-</Text>;
			// Parse as UTC and convert to IST (UTC+5:30)
			const istTime = moment.utc(text).utcOffset('+05:30');
			if (!istTime.isValid()) return <Text>-</Text>;
			return <Text>{istTime.format('DD/MM/YYYY,  h:mm a')}</Text>;
		},
	},
	{
		title: 'Created by',
		dataIndex: 'created_by_name',
		key: 'created_by_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (isActive, record) => (
			<Switch
				key={`status-${record.id}-${isActive}`}
				checked={isActive}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				onChange={() => handleStatusToggle(record)}
			/>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id) => (
			<ViewButton onClick={() => navigate(`/client/edit/${id}`)}>
				<EyeOutlined />
			</ViewButton>
		),
	},
];

export default function ClientListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);

	// Data states
	const [clientData, setClientData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = usePersistedState('clientList:pagination', {
		current: 1,
		pageSize: 10,
		total: 0,
	});

	// Search state
	const [searchTerm, setSearchTerm] = usePersistedState('clientList:search', '');

	// Filter states (auto-apply - no temporary state needed)
	// dateRange holds moment objects (not JSON-safe), so it stays in-memory.
	const [dateRange, setDateRange] = useState([null, null]);
	const [statusFilters, setStatusFilters] = usePersistedState('clientList:statusFilters', {
		all: false,
		active: false,
		inactive: false,
	});
	const [businessUnitFilters, setBusinessUnitFilters] = usePersistedState(
		'clientList:businessUnits',
		[]
	);
	const [cityFilter, setCityFilter] = usePersistedState('clientList:city', undefined);
	const [citiesList, setCitiesList] = useState([]);
	const [legalStructureFilters, setLegalStructureFilters] = usePersistedState(
		'clientList:legalStructure',
		[]
	);
	const [costCenterFilters, setCostCenterFilters] = usePersistedState(
		'clientList:costCenter',
		[]
	);

	// Status change modal states
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [selectedClient, setSelectedClient] = useState(null);
	const [selectedClientName, setSelectedClientName] = useState('');
	const [newStatus, setNewStatus] = useState(false);
	const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

	// Helper: resolve filter value from overrides or fallback to current state
	const getFilterValue = (overrides, key, fallback) =>
		Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : fallback;

	// Build filter object with explicit parameters
	const buildFilters = (overrideFilters = {}) => {
		const filters = {};

		const currentSearch = getFilterValue(overrideFilters, 'searchTerm', searchTerm);
		const currentDateRange = getFilterValue(overrideFilters, 'dateRange', dateRange);
		const currentStatusFilters = getFilterValue(
			overrideFilters,
			'statusFilters',
			statusFilters
		);
		const currentBusinessUnitFilters = getFilterValue(
			overrideFilters,
			'businessUnitFilters',
			businessUnitFilters
		);
		const currentCityFilter = getFilterValue(overrideFilters, 'cityFilter', cityFilter);
		const currentLegalStructureFilters = getFilterValue(
			overrideFilters,
			'legalStructureFilters',
			legalStructureFilters
		);
		const currentCostCenterFilters = getFilterValue(
			overrideFilters,
			'costCenterFilters',
			costCenterFilters
		);

		if (currentSearch) {
			filters.query_str = currentSearch;
		}
		if (currentDateRange && currentDateRange[0]) {
			filters.created_from = moment(currentDateRange[0]).startOf('day').toISOString();
		}
		if (currentDateRange && currentDateRange[1]) {
			filters.created_to = moment(currentDateRange[1]).endOf('day').toISOString();
		}
		if (currentStatusFilters.active && !currentStatusFilters.inactive) {
			filters.is_active = true;
		} else if (currentStatusFilters.inactive && !currentStatusFilters.active) {
			filters.is_active = false;
		}
		if (currentBusinessUnitFilters.length > 0) {
			filters.business_unit_ids = currentBusinessUnitFilters;
		}
		if (currentCityFilter) {
			filters.city = currentCityFilter;
		}
		if (currentLegalStructureFilters.length > 0) {
			filters.legal_structure = currentLegalStructureFilters;
		}
		if (currentCostCenterFilters.length === 1) {
			[filters.primary_cost_center] = currentCostCenterFilters;
		}

		return filters;
	};

	// Handle status toggle click (opens confirmation modal)
	const handleStatusToggle = (record) => {
		setSelectedClient(record.id);
		setSelectedClientName(record.client_legal_name || record.company_name || 'Unknown');
		setNewStatus(!record.is_active);
		setStatusModalVisible(true);
	};

	// Handle confirmation of status change
	const handleConfirmStatusChange = async () => {
		setStatusUpdateLoading(true);
		try {
			await dispatch(
				updateClientStatus({
					id: selectedClient,
					is_active: newStatus,
				})
			);
			// Optimistically update the local state immediately
			setClientData((prevData) =>
				prevData.map((client) =>
					client.id === selectedClient ? { ...client, is_active: newStatus } : client
				)
			);
			// Refresh the client list after successful update to ensure data consistency
			await fetchClients(pagination.current, pagination.pageSize);
		} catch (error) {
			console.error('Error updating client status:', error);
			// Revert optimistic update on error by refreshing
			await fetchClients(pagination.current, pagination.pageSize);
		} finally {
			setStatusUpdateLoading(false);
			setStatusModalVisible(false);
			setSelectedClient(null);
			setSelectedClientName('');
			setNewStatus(false);
		}
	};

	// Handle cancellation of status change
	const handleCancelStatusChange = () => {
		setStatusModalVisible(false);
		setSelectedClient(null);
		setSelectedClientName('');
		setNewStatus(false);
	};

	// Fetch clients from API with optional filter overrides
	const fetchClients = async (page = 1, pageSize = 10, filterOverrides = {}) => {
		setLoading(true);
		try {
			const filters = buildFilters(filterOverrides);
			const result = await dispatch(
				getClientListV2({
					filter_by: filters,
					pagination: { page, per_page: pageSize },
					sort_by: { field: 'created_at', order: 'desc' },
				})
			);

			setClientData(result.rows || []);
			setPagination({
				current: result.pagination?.page || page,
				pageSize: result.pagination?.per_page || pageSize,
				total: result.count || 0,
			});
		} catch (error) {
			console.error('Error fetching clients:', error);
		} finally {
			setLoading(false);
		}
	};

	// Fetch cities list on mount using V2 API (fetch all pages to get all cities)
	useEffect(() => {
		const fetchAllCities = async () => {
			try {
				// First fetch to get total_pages
				const firstRes = await dispatch(
					getClientListV2({
						filter_by: {},
						pagination: { page: 1, per_page: 20 },
					})
				);
				let allClients = [...(firstRes?.rows || [])];
				const totalPages = firstRes?.pagination?.total_pages || 1;
				if (totalPages > 1) {
					const promises = [];
					for (let page = 2; page <= totalPages; page += 1) {
						promises.push(
							dispatch(
								getClientListV2({
									filter_by: {},
									pagination: { page, per_page: 20 },
								})
							)
						);
					}
					const results = await Promise.all(promises);
					results.forEach((res) => {
						allClients = [...allClients, ...(res?.rows || [])];
					});
				}
				const allCities = allClients.map((client) => client?.head_office_address?.city);
				const uniqueCities = [...new Set(allCities.filter(Boolean))].sort();
				const cityOptions = uniqueCities.map((city) => ({
					value: city,
					label: city,
				}));
				setCitiesList(cityOptions);
			} catch (error) {
				console.error('[CityFilter] Error fetching cities:', error);
			}
		};
		fetchAllCities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	// Load data on mount
	useEffect(() => {
		fetchClients(pagination.current, pagination.pageSize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSearch = (value) => {
		setSearchTerm(value);
		// Immediately fetch with new search term (pass it directly to avoid state update delay)
		fetchClients(1, pagination.pageSize, { searchTerm: value });
	};

	const handleSync = () => {
		fetchClients(pagination.current, pagination.pageSize);
	};

	const handleExport = async () => {
		setLoading(true);
		try {
			await dispatch(
				exportClientCSV({
					filter_by: buildFilters(),
				})
			);
			// Download is automatically triggered in the slice function
		} catch (error) {
			console.error('Error exporting clients:', error);
			message.error('Failed to export clients. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		navigate('/client/addsection');
	};

	const handleClearFilters = () => {
		// Clear ONLY filters (NOT search term)
		// This is called when clicking X icon next to filter tags
		setDateRange([null, null]);
		setStatusFilters({ all: false, active: false, inactive: false });
		setBusinessUnitFilters([]);
		setCityFilter(undefined);
		setLegalStructureFilters([]);
		setCostCenterFilters([]);
		// DON'T clear search term - keep it active
		// Fetch data with cleared filters but keeping search term
		fetchClients(1, pagination.pageSize, {
			dateRange: [null, null],
			statusFilters: { all: false, active: false, inactive: false },
			businessUnitFilters: [],
			cityFilter: undefined,
			legalStructureFilters: [],
			costCenterFilters: [],
		});
	};

	// Handlers to remove individual filters
	const handleRemoveDateFilter = () => handleDateRangeChange([null, null]);
	const handleRemoveStatusFilter = () => {
		const newStatusFilters = { all: false, active: false, inactive: false };
		setStatusFilters(newStatusFilters);
		fetchClients(1, pagination.pageSize, { statusFilters: newStatusFilters });
	};
	const handleRemoveBusinessUnitFilter = () => {
		setBusinessUnitFilters([]);
		fetchClients(1, pagination.pageSize, { businessUnitFilters: [] });
	};
	const handleRemoveCityFilter = () => handleCityChange(undefined);
	const handleRemoveLegalStructureFilter = () => handleLegalStructureChange([]);
	const handleRemoveCostCenterFilter = () => handleCostCenterChange([]);

	const handleDrawerClose = () => {
		setOpen(false);
	};

	const handleDrawerOpen = () => {
		setOpen(true);
	};

	const handleTableChange = (paginationConfig) => {
		fetchClients(paginationConfig.current, paginationConfig.pageSize);
	};

	// Calculate active filter tags for display
	const dateFilterValue = dateRange[0] || dateRange[1] ? 'Date Range' : '';
	const statusFilterValue = statusFilters.active || statusFilters.inactive ? 'Status' : '';
	const businessUnitFilterValue = businessUnitFilters.length > 0 ? 'Business Unit' : '';
	const cityFilterValue = cityFilter ? 'City' : '';
	const legalStructureFilterValue = legalStructureFilters.length > 0 ? 'Legal Structure' : '';
	const costCenterFilterValue = costCenterFilters.length > 0 ? 'Cost Center' : '';

	// Calculate active filters count (EXCLUDING search)
	// Search is separate from filters and shouldn't be counted here
	const activeFiltersCount = [
		dateRange[0] || dateRange[1],
		statusFilters.active || statusFilters.inactive,
		businessUnitFilters.length > 0,
		!!cityFilter,
		legalStructureFilters.length > 0,
		costCenterFilters.length > 0,
	].filter(Boolean).length;

	const handleStatusChange = (key, checked) => {
		const newStatusFilters = {
			...statusFilters,
			[key]: checked,
		};
		setStatusFilters(newStatusFilters);
		// Auto-apply filter
		fetchClients(1, pagination.pageSize, {
			statusFilters: newStatusFilters,
		});
	};

	const handleDateRangeChange = (dates) => {
		setDateRange(dates || [null, null]);
		// Auto-apply filter
		fetchClients(1, pagination.pageSize, {
			dateRange: dates || [null, null],
		});
	};

	const handleCityChange = (val) => {
		setCityFilter(val || undefined);
		// Auto-apply filter
		fetchClients(1, pagination.pageSize, {
			cityFilter: val || undefined,
		});
	};

	const handleLegalStructureChange = (checkedValues) => {
		setLegalStructureFilters(checkedValues);
		// Auto-apply filter
		fetchClients(1, pagination.pageSize, {
			legalStructureFilters: checkedValues,
		});
	};

	const handleCostCenterChange = (checkedValues) => {
		setCostCenterFilters(checkedValues);
		// Auto-apply filter
		fetchClients(1, pagination.pageSize, {
			costCenterFilters: checkedValues,
		});
	};

	return (
		<>
			<Flex justifyContent="space-between" mt={3}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							enterButton="Search"
							placeholder="Search by Client Legal Name"
							allowClear
							onSearch={handleSearch}
							loading={loading}
						/>
					</Box>
					<StyledFilterButton onClick={handleDrawerOpen}>
						FILTERS {activeFiltersCount > 0 && `(${activeFiltersCount})`}
					</StyledFilterButton>
					<FilterDisplayBox>
						{dateFilterValue && (
							<FilterTag onClose={handleRemoveDateFilter}>Date Range</FilterTag>
						)}
						{statusFilterValue && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{businessUnitFilterValue && (
							<FilterTag onClose={handleRemoveBusinessUnitFilter}>
								Business Unit
							</FilterTag>
						)}
						{cityFilterValue && (
							<FilterTag onClose={handleRemoveCityFilter}>City</FilterTag>
						)}
						{legalStructureFilterValue && (
							<FilterTag onClose={handleRemoveLegalStructureFilter}>
								Legal Structure
							</FilterTag>
						)}
						{costCenterFilterValue && (
							<FilterTag onClose={handleRemoveCostCenterFilter}>
								Cost Center
							</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleClearFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex>
					<ButtonWrapper
						onClick={handleExport}
						icons={<ExportOutlined />}
						loading={loading}
					>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />} loading={loading}>
						SYNC
					</ButtonWrapper>
					<CreateButton onClick={handleCreate}>
						<PlusOutlined />
						<span>CREATE</span>
					</CreateButton>
				</Flex>
			</Flex>
			<Box mt={3}>
				<Container>
					<AntdTable
						rowKey="id"
						columns={getTableColumns(navigate, handleStatusToggle)}
						dataSource={clientData}
						loading={loading}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: pagination.total,
							showSizeChanger: true,
							showTotal: (total) => `Total ${total} clients`,
						}}
						onChange={handleTableChange}
					/>
				</Container>
			</Box>

			<Drawer
				className="userList-drawer"
				placement="right"
				closable={false}
				onClose={handleDrawerClose}
				open={open}
				title={null}
				headerStyle={{ display: 'none' }}
			>
				<Box>
					{/* Filter Header */}
					<FilterHeader>
						<FilterTitle>Filters</FilterTitle>
						<FilterCloseButton onClick={handleDrawerClose} />
					</FilterHeader>

					{/* Date Range Section */}
					<FilterSection>
						<FilterSectionTitle>Date Range</FilterSectionTitle>
						<StyledRangePicker
							value={dateRange}
							onChange={handleDateRangeChange}
							format="DD/MM/YYYY"
							placeholder={['Start Date', 'End Date']}
						/>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Status Section */}
					<FilterSection>
						<FilterSectionTitle>Status</FilterSectionTitle>
						<CheckboxWrapper>
							<StyledCheckbox
								checked={statusFilters.all}
								onChange={(e) => handleStatusChange('all', e.target.checked)}
							>
								All
							</StyledCheckbox>
							<StyledCheckbox
								checked={statusFilters.active}
								onChange={(e) => handleStatusChange('active', e.target.checked)}
							>
								Active
							</StyledCheckbox>
							<StyledCheckbox
								checked={statusFilters.inactive}
								onChange={(e) => handleStatusChange('inactive', e.target.checked)}
							>
								In-Active
							</StyledCheckbox>
						</CheckboxWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* City Section */}
					<FilterSection>
						<FilterSectionTitle>City</FilterSectionTitle>
						<StyledSelect
							showSearch
							name="city"
							value={cityFilter}
							onChange={handleCityChange}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select City"
							options={citiesList}
						/>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Legal Structure Section */}
					<FilterSection>
						<FilterSectionTitle>Legal Structure</FilterSectionTitle>
						<CheckboxWrapper>
							<StyledCheckbox
								checked={legalStructureFilters.includes('Individual')}
								onChange={(e) => {
									const newValues = e.target.checked
										? [...legalStructureFilters, 'Individual']
										: legalStructureFilters.filter((v) => v !== 'Individual');
									handleLegalStructureChange(newValues);
								}}
							>
								Individual
							</StyledCheckbox>
							<StyledCheckbox
								checked={legalStructureFilters.includes('LLP')}
								onChange={(e) => {
									const newValues = e.target.checked
										? [...legalStructureFilters, 'LLP']
										: legalStructureFilters.filter((v) => v !== 'LLP');
									handleLegalStructureChange(newValues);
								}}
							>
								LLP
							</StyledCheckbox>
							<StyledCheckbox
								checked={legalStructureFilters.includes('Company')}
								onChange={(e) => {
									const newValues = e.target.checked
										? [...legalStructureFilters, 'Company']
										: legalStructureFilters.filter((v) => v !== 'Company');
									handleLegalStructureChange(newValues);
								}}
							>
								Company
							</StyledCheckbox>
						</CheckboxWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Primary Cost Center Section */}
					<FilterSection>
						<FilterSectionTitle>Primary Cost Center</FilterSectionTitle>
						<CheckboxWrapper>
							<StyledCheckbox
								checked={costCenterFilters.includes('Logistics')}
								onChange={(e) => {
									const newValues = e.target.checked
										? [...costCenterFilters, 'Logistics']
										: costCenterFilters.filter((v) => v !== 'Logistics');
									handleCostCenterChange(newValues);
								}}
							>
								Logistics
							</StyledCheckbox>
							<StyledCheckbox
								checked={costCenterFilters.includes('Distribution')}
								onChange={(e) => {
									const newValues = e.target.checked
										? [...costCenterFilters, 'Distribution']
										: costCenterFilters.filter((v) => v !== 'Distribution');
									handleCostCenterChange(newValues);
								}}
							>
								Distribution
							</StyledCheckbox>
						</CheckboxWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Action Buttons */}
					<Box mt={3}>
						<ResetButton onClick={handleClearFilters}>Reset Filters</ResetButton>
					</Box>
				</Box>
			</Drawer>

			{/* Status Confirmation Modal */}
			<StatusChangeModal
				visible={statusModalVisible}
				entityId={selectedClient}
				entityName="client"
				entityDisplayName={selectedClientName}
				newStatus={newStatus}
				onConfirm={handleConfirmStatusChange}
				onCancel={handleCancelStatusChange}
				loading={statusUpdateLoading}
			/>
		</>
	);
}
